import { AfterViewInit, Directive, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, debounce, distinctUntilChanged, filter, takeUntil, timer } from 'rxjs';

export enum DelayEvent {
  MouseEnter = 'mouseenter',
  MouseLeave = 'mouseleave',
  ForceHide = 'forcehide',
}

@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[nrcHoverDelay]'
})
export class HoverDelayDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() public debounceTimeMouseEnter = 0; // Default debounce time for "mouseenter" events in milliseconds.
  @Input() public debounceTimeMouseLeave = 0; // Default debounce time for "mouseleave" events in milliseconds.
  @Input() public targetId!: string; // ID of the element to apply the delay to.
  @Input() public trackTargetHover = false; // When true, also track hover on the target menu to keep it open.
  @Input() public hideOnClick = false; // When true, hide the target when clicking inside it.

  private delay$ = new Subject<DelayEvent>();
  private destroy$ = new Subject<void>();
  private targetMenuElement: HTMLElement | null = null;
  private boundTargetMouseEnter: (() => void) | null = null;
  private boundTargetMouseLeave: (() => void) | null = null;
  private boundTargetClick: (() => void) | null = null;

  /**
   *
   */
  public ngOnInit(): void {
    // Handle normal hover events with debounce
    this.delay$.pipe(
      filter(event => event !== DelayEvent.ForceHide),
      debounce(delayEvent => timer(delayEvent === DelayEvent.MouseEnter ? this.debounceTimeMouseEnter : this.debounceTimeMouseLeave)),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(delayEvent => {
      this.applyVisibilityState(delayEvent === DelayEvent.MouseEnter);
    });

    // Handle force hide events immediately without debounce
    this.delay$.pipe(
      filter(event => event === DelayEvent.ForceHide),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyVisibilityState(false);
    });
  }

  /**
   * Apply the visibility state to the target element.
   *
   * @param visible Whether to show or hide the element.
   */
  private applyVisibilityState(visible: boolean): void {
    const targetMenuElement = document.getElementById(this.targetId);

    if (!targetMenuElement) {
      return;
    }

    const toggleFrom = visible ? 'hidden' : 'visible';
    const toggleTo = visible ? 'visible' : 'hidden';

    if (targetMenuElement.classList.contains(toggleFrom)) {
      targetMenuElement.classList.toggle(toggleTo);
      targetMenuElement.classList.toggle(toggleFrom);
    }
  }

  /**
   * Programmatically force-hide the target element immediately.
   * This bypasses the debounce delay and hides the element instantly.
   */
  public forceHide(): void {
    this.delay$.next(DelayEvent.ForceHide);
  }

  /**
   * After view init, set up listeners on the target menu element if trackTargetHover or hideOnClick is enabled.
   */
  public ngAfterViewInit(): void {
    if (!this.trackTargetHover && !this.hideOnClick) {
      return;
    }

    // Use a small delay to ensure the portaled element is in the DOM
    setTimeout(() => this.setupTargetListeners(), 100);
  }

  /**
   * Set up hover and click listeners on the target menu element.
   * Hover listeners allow the menu to stay open when hovering over it.
   * Click listener hides the menu when clicking inside it.
   */
  private setupTargetListeners(): void {
    this.targetMenuElement = document.getElementById(this.targetId);

    if (!this.targetMenuElement) {
      return;
    }

    // Set up hover listeners if enabled
    if (this.trackTargetHover) {
      this.boundTargetMouseEnter = (): void => this.delay$.next(DelayEvent.MouseEnter);
      this.boundTargetMouseLeave = (): void => this.delay$.next(DelayEvent.MouseLeave);

      this.targetMenuElement.addEventListener('mouseenter', this.boundTargetMouseEnter);
      this.targetMenuElement.addEventListener('mouseleave', this.boundTargetMouseLeave);
    }

    // Set up click listener if enabled - hides menu immediately when clicking inside
    if (this.hideOnClick) {
      this.boundTargetClick = (): void => this.forceHide();
      this.targetMenuElement.addEventListener('click', this.boundTargetClick);
    }
  }

  /**
   * Clean up listeners when the directive is destroyed.
   */
  public ngOnDestroy(): void {
    // Clean up target menu hover listeners
    if (this.targetMenuElement && this.boundTargetMouseEnter && this.boundTargetMouseLeave) {
      this.targetMenuElement.removeEventListener('mouseenter', this.boundTargetMouseEnter);
      this.targetMenuElement.removeEventListener('mouseleave', this.boundTargetMouseLeave);
    }

    // Clean up target menu click listener
    if (this.targetMenuElement && this.boundTargetClick) {
      this.targetMenuElement.removeEventListener('click', this.boundTargetClick);
    }

    this.delay$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   *
   */
  @HostListener('mouseenter')
  public onMouseEnter(): void {
    this.delay$.next(DelayEvent.MouseEnter);
  }

  /**
   *
   */
  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.delay$.next(DelayEvent.MouseLeave);
  }
}
